import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import SearchBar from '../../components/ui/SearchBar';
import FilterPanel from './components/FilterPanel';
import SortControls from './components/SortControls';
import TrailerCard from './components/TrailerCard';
import SkeletonCard from './components/SkeletonCard';
import MobileFilterDrawer from './components/MobileFilterDrawer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { fetchTrailerListings } from '../../services/trailerListingService';
import { useAuth } from '../../contexts/AuthContext';

const SearchResults = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [trailers, setTrailers] = useState([]);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    location: location?.state?.searchData?.location || '',
    pickupDate: location?.state?.searchData?.pickupDate || '',
    returnDate: location?.state?.searchData?.returnDate || '',
    trailerType: location?.state?.searchData?.trailerType || '',
    minPrice: '',
    maxPrice: '',
    radius: '25',
    minPayload: '',
    hitchType: '',
    minLength: '',
    minWidth: '',
    electricBrakes: false,
    ramps: false,
    tieDowns: false,
    sideRails: false,
    spareTire: false
  });

  // Fetch trailers from database
  useEffect(() => {
    const loadTrailers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchTrailerListings({
          location: filters?.location,
          trailerType: filters?.trailerType,
          minPrice: filters?.minPrice,
          maxPrice: filters?.maxPrice,
          pickupDate: filters?.pickupDate,
          returnDate: filters?.returnDate
        });
        setTrailers(data || []);
      } catch (err) {
        console.error('Error loading trailers:', err);
        setError('Failed to load trailers. Please try again.');
        setTrailers([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTrailers();
  }, [filters?.location, filters?.trailerType, filters?.minPrice, filters?.maxPrice, filters?.pickupDate, filters?.returnDate]);

  // Generate SEO metadata based on search filters
  const generatePageTitle = () => {
    const parts = [];
    if (filters?.trailerType) parts?.push(filters?.trailerType);
    if (filters?.location) parts?.push(`in ${filters?.location}`);
    parts?.push('Trailer Rentals');
    return `${parts?.join(' ')} | Draggin Trailers`;
  };

  const generatePageDescription = () => {
    const location = filters?.location || 'your area';
    const type = filters?.trailerType || 'trailers';
    const count = trailers?.length;
    return `Find ${type} for rent in ${location}. Browse ${count > 0 ? count : ''} available trailers with instant booking. Compare prices, read reviews, and rent from verified local owners.`;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    "name": generatePageTitle(),
    "description": generatePageDescription(),
    "url": `https://draggintrailers.com/search${location?.search || ''}`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": trailers?.length,
      "itemListElement": trailers?.slice(0, 10)?.map((trailer, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": trailer?.title,
          "description": trailer?.description,
          "image": trailer?.photos?.[0],
          "offers": {
            "@type": "Offer",
            "price": trailer?.dailyRate,
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          }
        }
      }))
    }
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://draggintrailers.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Search Results",
        "item": `https://draggintrailers.com/search${location?.search || ''}`
      }
    ]
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      location: '',
      pickupDate: '',
      returnDate: '',
      trailerType: '',
      minPrice: '',
      maxPrice: '',
      radius: '25',
      minPayload: '',
      hitchType: '',
      minLength: '',
      minWidth: '',
      electricBrakes: false,
      ramps: false,
      tieDowns: false,
      sideRails: false,
      spareTire: false
    });
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const handleSearch = (searchData) => {
    setFilters((prev) => ({
      ...prev,
      location: searchData?.location,
      pickupDate: searchData?.pickupDate,
      returnDate: searchData?.returnDate,
      trailerType: searchData?.trailerType
    }));
  };

  // Detect if current location filter is a zipcode for radius search messaging
  const isZipcodeSearch = /^\d{5}(-\d{4})?$/?.test(filters?.location?.trim() || '');

  // Apply client-side filtering for features not handled by database query
  const filteredTrailers = trailers?.filter((trailer) => {
    // Additional client-side filters can be added here
    return true;
  });

  const sortedTrailers = [...filteredTrailers]?.sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a?.dailyRate - b?.dailyRate;
      case 'price-high':
        return b?.dailyRate - a?.dailyRate;
      case 'rating':
        return (b?.averageRating || 0) - (a?.averageRating || 0);
      case 'distance':
        // Distance sorting would require geolocation calculation
        return 0;
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{generatePageTitle()}</title>
        <meta name="description" content={generatePageDescription()} />
        <meta name="keywords" content={`trailer rental, ${filters?.trailerType || 'trailers'}, ${filters?.location || 'local'}, rent trailer, cargo trailer, utility trailer, equipment rental`} />
        <link rel="canonical" href={`https://draggintrailers.com/search${location?.search || ''}`} />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={generatePageTitle()} />
        <meta property="og:description" content={generatePageDescription()} />
        <meta property="og:url" content={`https://draggintrailers.com/search${location?.search || ''}`} />
        <meta property="og:site_name" content="Draggin Trailers" />
        <meta property="og:image" content="https://draggintrailers.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={generatePageTitle()} />
        <meta name="twitter:description" content={generatePageDescription()} />
        <meta name="twitter:image" content="https://draggintrailers.com/twitter-image.jpg" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      </Helmet>
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      <div className="pt-20">
        <BreadcrumbNavigation />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
              Available Trailers
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground">
              {isZipcodeSearch && filters?.location
                ? `Showing trailers within 25 miles of ${filters?.location}`
                : 'Find the perfect trailer for your needs from local owners'}
            </p>
            {isZipcodeSearch && filters?.location && (
              <div className="mt-3 inline-flex items-center space-x-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                <Icon name="MapPin" size={14} strokeWidth={2} />
                <span>25-mile radius search from ZIP {filters?.location}</span>
              </div>
            )}
          </div>

          <div className="mb-6 md:mb-8">
            <SearchBar onSearch={handleSearch} />
          </div>

          <div className="hidden md:block mb-6">
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              resultCount={sortedTrailers?.length}
              onClearFilters={handleClearFilters} />
            
          </div>

          <div className="md:hidden mb-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setIsMobileFilterOpen(true)}
              iconName="SlidersHorizontal"
              iconPosition="left"
              fullWidth>
              
              Filters ({sortedTrailers?.length} results)
            </Button>
          </div>

          <SortControls
            sortBy={sortBy}
            onSortChange={handleSortChange}
            viewMode={viewMode}
            onViewModeChange={setViewMode} />
          

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <p className="font-body text-sm">{error}</p>
            </div>
          )}

          {isLoading ?
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6' : 'space-y-4 md:space-y-6'
          }>
              {[1, 2, 3, 4, 5, 6]?.map((i) =>
            <SkeletonCard key={i} viewMode={viewMode} />
            )}
            </div> :
          sortedTrailers?.length > 0 ?
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6' : 'space-y-4 md:space-y-6'
          }>
              {sortedTrailers?.map((trailer) =>
            <TrailerCard
              key={trailer?.id}
              trailer={trailer}
              viewMode={viewMode} />

            )}
            </div> :

          <div className="text-center py-12 md:py-16">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <Icon name="Search" size={40} strokeWidth={2} className="text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground mb-2">
                No Trailers Found
              </h3>
              <p className="font-body text-muted-foreground mb-6 max-w-md mx-auto">
                {isZipcodeSearch && filters?.location
                  ? `No trailers found within 25 miles of ZIP code ${filters?.location}. Try a different location or clear filters to see all available trailers.`
                  : "We couldn't find any trailers matching your search criteria. Try adjusting your filters or search parameters."}
              </p>
              <Button
              variant="default"
              size="lg"
              onClick={handleClearFilters}
              iconName="RotateCcw"
              iconPosition="left">
              
                Clear All Filters
              </Button>
            </div>
          }
        </div>
      </div>
      <MobileFilterDrawer
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        resultCount={sortedTrailers?.length}
        onClearFilters={handleClearFilters} />
    </div>
  );

};

export default SearchResults;